import { useState } from 'react';
import { AI_STRATEGIES } from '@sheepocalypse/shared';
import { useLobbyStore, useSocketStore } from '../../stores/socketStore';
import { LobbyCreator } from './LobbyCreator';
import { LobbyList } from './LobbyList';
import { PLAYER_COLOR_OPTIONS } from './playerColors';

export function LobbyView() {
  const [view, setView] = useState<'list' | 'create'>('list');
  const { currentLobby } = useLobbyStore();

  if (currentLobby) {
    return <LobbyRoom />;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 to-blue-50 p-8">
      <div className="max-w-4xl mx-auto">
        <header className="text-center mb-12">
          <h1 className="text-6xl font-bold text-gray-800 mb-4">Sheepocalypse</h1>
          <p className="text-xl text-gray-600">A turn-based strategy game for friends</p>
        </header>

        <div className="bg-white rounded-2xl shadow-xl p-8">
          <div className="flex justify-center space-x-4 mb-8">
            <button
              data-testid="lobby-tab-join"
              onClick={() => setView('list')}
              className={`px-6 py-3 rounded-lg font-medium transition-colors ${
                view === 'list'
                  ? 'bg-blue-500 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              Join Game
            </button>
            <button
              data-testid="lobby-tab-create"
              onClick={() => setView('create')}
              className={`px-6 py-3 rounded-lg font-medium transition-colors ${
                view === 'create'
                  ? 'bg-green-500 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              Create Game
            </button>
          </div>

          {view === 'list' ? <LobbyList /> : <LobbyCreator />}
        </div>
      </div>
    </div>
  );
}

function LobbyRoom() {
  const [aiStrategy, setAiStrategy] = useState('balanced');
  const [boardWidthInput, setBoardWidthInput] = useState<number | null>(null);
  const [boardHeightInput, setBoardHeightInput] = useState<number | null>(null);
  const [copyFeedback, setCopyFeedback] = useState<string | null>(null);
  const { currentLobby, playerId } = useLobbyStore();
  const { createGame, configureLobby, setPlayerColor, addAIPlayer, leaveLobby } = useSocketStore();

  if (!currentLobby) return null;

  const isHost = currentLobby.hostId === playerId;
  const players = Object.values(currentLobby.players);
  const activePlayerCount = players.filter(player => !player.isSpectator).length;
  const allReady = players.every(p => p.isReady);
  const currentBoardSize = currentLobby.rules?.board?.size;
  const boardWidth = boardWidthInput ?? currentBoardSize?.width ?? 8;
  const boardHeight = boardHeightInput ?? currentBoardSize?.height ?? 8;
  const boardSizeIsValid =
    Number.isInteger(boardWidth) &&
    Number.isInteger(boardHeight) &&
    boardWidth >= 2 &&
    boardWidth <= 12 &&
    boardHeight >= 2 &&
    boardHeight <= 12;

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 to-blue-50 p-8">
      <div className="max-w-2xl mx-auto">
        <div className="bg-white rounded-2xl shadow-xl p-8">
          <h2 className="text-3xl font-bold text-gray-800 mb-2">{currentLobby.name}</h2>
          <div className="text-gray-500 mb-8 flex items-center gap-3">
            <p>
              Code:{' '}
              <span data-testid="lobby-code" className="font-mono font-bold">
                {currentLobby.code}
              </span>
            </p>
            <button
              data-testid="copy-lobby-code-button"
              type="button"
              onClick={async () => {
                try {
                  await navigator.clipboard.writeText(currentLobby.code);
                  setCopyFeedback('Copied');
                } catch {
                  setCopyFeedback('Copy failed');
                }

                setTimeout(() => {
                  setCopyFeedback(null);
                }, 1500);
              }}
              className="px-2 py-1 text-xs bg-gray-100 rounded border border-gray-300 hover:bg-gray-200"
            >
              Copy
            </button>
            {copyFeedback && (
              <span data-testid="lobby-code-copy-feedback" className="text-xs text-gray-500">
                {copyFeedback}
              </span>
            )}
          </div>

          <div className="mb-6 p-4 bg-slate-50 rounded-lg">
            <p className="text-sm text-slate-700" data-testid="lobby-board-size-display">
              Board: {currentBoardSize?.width ?? 8} x {currentBoardSize?.height ?? 8}
            </p>
            {isHost && (
              <div className="mt-3 space-y-3">
                <label className="block text-sm font-medium text-gray-700 mb-2">Board Size</label>
                <div className="grid grid-cols-2 gap-2">
                  <input
                    data-testid="lobby-board-width-input"
                    type="number"
                    min={2}
                    max={12}
                    value={boardWidth}
                    onChange={e => setBoardWidthInput(Number(e.target.value))}
                    className="px-4 py-2 border border-gray-300 rounded-lg"
                    aria-label="Board width"
                  />
                  <input
                    data-testid="lobby-board-height-input"
                    type="number"
                    min={2}
                    max={12}
                    value={boardHeight}
                    onChange={e => setBoardHeightInput(Number(e.target.value))}
                    className="px-4 py-2 border border-gray-300 rounded-lg"
                    aria-label="Board height"
                  />
                </div>
                <div className="flex gap-2 mt-2">
                  {[
                    { w: 5, h: 6 },
                    { w: 6, h: 6 },
                    { w: 7, h: 7 },
                  ].map(preset => (
                    <button
                      key={`${preset.w}x${preset.h}`}
                      type="button"
                      data-testid={`lobby-board-preset-${preset.w}x${preset.h}`}
                      onClick={() => {
                        setBoardWidthInput(preset.w);
                        setBoardHeightInput(preset.h);
                      }}
                      className="px-3 py-1 text-xs bg-gray-100 rounded"
                    >
                      {preset.w}x{preset.h}
                    </button>
                  ))}
                </div>
                <button
                  data-testid="lobby-board-size-apply"
                  type="button"
                  disabled={!boardSizeIsValid}
                  onClick={() => {
                    if (!boardSizeIsValid) {
                      return;
                    }

                    configureLobby({
                      rules: {
                        board: {
                          size: { width: boardWidth, height: boardHeight },
                        },
                      },
                    });
                  }}
                  className="px-4 py-2 bg-blue-500 text-white rounded-lg disabled:opacity-50"
                >
                  Apply Board Size
                </button>
                {!boardSizeIsValid && (
                  <p data-testid="lobby-board-size-error" className="text-xs text-red-600">
                    Width/Height must be whole numbers between 2 and 12.
                  </p>
                )}

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Add AI Player
                  </label>
                  <div className="flex gap-2">
                    <select
                      data-testid="lobby-add-ai-strategy-select"
                      value={aiStrategy}
                      onChange={e => setAiStrategy(e.target.value)}
                      className="flex-1 px-4 py-2 border border-gray-300 rounded-lg"
                    >
                      {AI_STRATEGIES.map(strategy => (
                        <option key={strategy} value={strategy}>
                          {strategy}
                        </option>
                      ))}
                    </select>
                    <button
                      data-testid="lobby-add-ai-button"
                      type="button"
                      disabled={activePlayerCount >= currentLobby.maxPlayers}
                      onClick={() => addAIPlayer({ strategy: aiStrategy })}
                      className="px-4 py-2 bg-indigo-500 text-white rounded-lg disabled:opacity-50"
                    >
                      Add AI
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>

          <div className="space-y-4 mb-8">
            <h3 className="text-lg font-semibold text-gray-700">
              Players ({activePlayerCount}/{currentLobby.maxPlayers})
            </h3>
            {players.map(player => (
              <div
                key={player.id}
                data-testid={`lobby-player-${player.id}`}
                className="flex items-center justify-between p-4 bg-gray-50 rounded-lg"
              >
                <div className="flex items-center space-x-3">
                  <div className="w-4 h-4 rounded-full" style={{ backgroundColor: player.color }} />
                  <span data-testid={`lobby-player-name-${player.id}`} className="font-medium">
                    {player.name}
                  </span>
                  {player.isAI && (
                    <span className="text-xs bg-indigo-100 text-indigo-700 px-2 py-1 rounded">
                      AI ({player.aiStrategy ?? 'balanced'})
                    </span>
                  )}
                  {player.isSpectator && (
                    <span className="text-xs bg-slate-100 text-slate-700 px-2 py-1 rounded">
                      Spectator
                    </span>
                  )}
                  {player.isHost && (
                    <span
                      data-testid={`lobby-player-host-${player.id}`}
                      className="text-xs bg-yellow-100 text-yellow-800 px-2 py-1 rounded"
                    >
                      Host
                    </span>
                  )}
                </div>
                <div className="flex items-center space-x-2">
                  {player.id === playerId && (
                    <select
                      data-testid="lobby-player-color-select"
                      value={player.color}
                      onChange={e => setPlayerColor(e.target.value)}
                      className="text-xs px-2 py-1 border border-gray-300 rounded"
                    >
                      {PLAYER_COLOR_OPTIONS.map(colorOption => {
                        const isUsedByOtherPlayer = players.some(
                          lobbyPlayer =>
                            lobbyPlayer.id !== player.id && lobbyPlayer.color === colorOption.value
                        );

                        return (
                          <option
                            key={colorOption.value}
                            value={colorOption.value}
                            disabled={isUsedByOtherPlayer}
                          >
                            {colorOption.label}
                          </option>
                        );
                      })}
                    </select>
                  )}
                  {player.isReady ? (
                    <span
                      data-testid={`lobby-player-ready-${player.id}`}
                      className="text-green-600 text-sm"
                    >
                      Ready
                    </span>
                  ) : (
                    <span
                      data-testid={`lobby-player-ready-${player.id}`}
                      className="text-gray-400 text-sm"
                    >
                      Not Ready
                    </span>
                  )}
                </div>
              </div>
            ))}
          </div>

          {isHost ? (
            <div className="space-y-3">
              <button
                data-testid="start-game-button"
                onClick={createGame}
                disabled={!allReady}
                className="w-full py-4 bg-green-500 text-white rounded-lg font-bold text-lg hover:bg-green-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {allReady ? 'Start Game' : 'Waiting for players...'}
              </button>
              <button
                data-testid="leave-lobby-button"
                type="button"
                onClick={leaveLobby}
                className="w-full py-3 bg-gray-100 hover:bg-gray-200 text-gray-800 rounded-lg font-medium transition-colors"
              >
                Leave Lobby
              </button>
            </div>
          ) : (
            <div className="space-y-3">
              <div data-testid="waiting-for-host" className="text-center text-gray-500">
                Waiting for host to start...
              </div>
              <button
                data-testid="leave-lobby-button"
                type="button"
                onClick={leaveLobby}
                className="w-full py-3 bg-gray-100 hover:bg-gray-200 text-gray-800 rounded-lg font-medium transition-colors"
              >
                Leave Lobby
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
